import React, { useEffect, useState, useMemo } from 'react';
import Breadcrumbs from '../../components/BreadCrumbs';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../components/Button';
import RewardSettingPopup from './RewardSettingPopup';
import Spinner from '../../components/Spinner';
import {
  fetchRedeemRewardsAuthorizationRequest,
  fetchRedeemRewardsRequest,
  webFormatCurrency,
  redeemRewardSearchRequest,
  redeemRewardsPointsRequest,
  rewardsRequest,
  useProductName,
  RedeemRewardsAuthorization,
  authorizationReprocessRequest,
  updateStateAfterRedeeption,
  contactInfoRequest,
  environment,
  renderDescriptionParagraphs,
} from '@rb-serve-app/core';
import CardDropdown from '../../components/CardDropdown';
import CommanToolTip from '../../components/CommanToolTip';
import moment from 'moment';
import RewardTransactionItem from '../../components/RewardTransactionItem';
import CommanModal from '../../components/CommanModal';
import Modal from 'react-modal';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { EventType, useAnalytics } from '../../ui/Analytics';
import SuccessModel from '../../components/SuccessModel';
import { logBrazeCustomEvent } from '../../config/braze';

const formatDateToYearMonthDay = (date: any) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatMobileDisplayDate = (dateString: string, locale: string) => {
  return moment.utc(dateString).locale(locale).format('dddd MMMM D, YYYY');
};

const formatTableDisplayDate = (dateString: string) => {
  return moment.utc(dateString).format('MMM D, YYYY');
};

const CashbackRewards: React.FC = () => {
  const { trackEvent } = useAnalytics();
  const lang = localStorage.getItem('language') || 'en-US';
  const setupLocale = () => {
    try {
      const localeToUse = lang?.includes('fr') ? 'fr' : 'en';
      moment.locale(localeToUse);
      return localeToUse;
    } catch (error) {
      console.warn('Error setting moment locale:', error);
      moment.locale('en');
      return 'en';
    }
  };
  const locale = setupLocale();
  const currentDate = new Date();
  const previous90DaysDate = new Date(currentDate);
  previous90DaysDate.setDate(currentDate.getDate() - 90);
  const fromDate = formatDateToYearMonthDay(previous90DaysDate);
  const toDate = formatDateToYearMonthDay(currentDate);
  const dispatch = useDispatch();
  const { contentfulData } = useSelector(
    (state: { contentful: any }) => state.contentful
  );
  const { accountId, accountDetails, customerId } = useSelector(
    (state: any) => state.user
  );
  const { rewards, loading, error } = useSelector(
    (state: any) => state.rewards
  );
  const { contactDetails, loading: contactDetailsLoading } = useSelector(
    (state: any) => state.contactInfo
  );
  const {
    redeemRewardsAuthorizationList,
    redeemRewardsAuthorizationLoding,
    authorizationReprocessLoding,
    authorizationReprocess,
    authorizationReprocessError,
    redeemRewardSearchData,
    redeemRewardsError,
    authorizationerror,
    redeemRewardSearchError,
    redeemRewardSearchLoading,
  } = useSelector((state: any) => state.redeemRewards);
  const { cardStructure, productName } = useProductName();
  const { entries, assets } = contentfulData;
  const {
    rb_rewards,
    rb_common,
    rb_transaction_history,
    rb_rewards_cashback,
    rb_error,
    rb_links,
  } = entries;
  const { rewardBalance, redemptionBonusAmount } =
    rewards?.pointsDetailsResponse || {};
     const productWithCardLast4 = [
    productName,
    accountDetails?.customer?.cardLast4,
  ]
    ?.filter(Boolean)
    ?.join(' ');


  const [isRewardSettingsOpen, setIsRewardSettingsOpen] = useState(false);
  const [availableCards, setAvailableCards] = useState<any>([]);
  const [selectedCard, setSelectedCard] = useState<any>(null);
  const [confirmRedeemVisible, setConfirmRedeemVisible] = useState(false);
  const [redeemStatusVisible, setRedeemStatusVisible] = useState(false);
  const [redeemErrorVisible, setRedeemErrorVisible] = useState(false);
  const [authorizationFail, setAuthorizationFail] = useState(false);
  const [
    isSelectionAuthorizationIndexData,
    setSelectionAuthorizationIndexData,
  ] = useState<RedeemRewardsAuthorization | null>(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [showApiErrorBanner, setShowApiErrorBanner] = useState(false);
  const [rewardSettingSuccessModal, setRewardSettingSuccessModal] =
    useState(false);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleClickEvent = (action: string) => {
    trackEvent(
      EventType.CLICK_EVENT,
      ['earned cash back rewards', 'rewards'],
      window.location.href,
      `earned cash back rewards | ${action}`
    );
  };

  useEffect(() => {
    trackEvent(
      EventType.PAGE_VIEW,
      ['earned cash back rewards', 'rewards'],
      window.location.href,
      undefined
    );
    logBrazeCustomEvent('viewed_redeem_rewards');
  }, []);

  useEffect(() => {
    if (rewardSettingSuccessModal) {
      const timeout = setTimeout(() => {
        setRewardSettingSuccessModal(false);
      }, 4000);
      return () => clearTimeout(timeout);
    }
  }, [rewardSettingSuccessModal]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 600) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (accountId) {
      dispatch(rewardsRequest({ accountId }));
    }
  }, [accountId, dispatch]);

  useEffect(() => {
    if (!contactDetails?.availableCards) {
      dispatch(
        contactInfoRequest({
          accountId: accountId,
          primaryCustomerId: customerId,
          customerId: customerId,
        })
      );
    }
  }, [accountId, customerId]);

  useEffect(() => {
    if (selectedCard?.cardNumber) {
      fetchAuthorizationList(fromDate, toDate);
    }
  }, [selectedCard, fromDate, toDate]);

  useEffect(() => {
    const redeemRewardPayload = {
      accId: accountId.replace(/^0+/, ''),
      cardLastFourDigits: selectedCard?.cardNumber.slice(-4),
      redemptionType: 'PWR',
    };
    if (selectedCard?.cardNumber) {
      dispatch(fetchRedeemRewardsRequest(redeemRewardPayload));
    }
  }, [selectedCard, accountId, dispatch]);
  const recaptchaLang = localStorage.getItem('language') === 'fr' ? 'fr' : 'en';

  const groupedTransactions = useMemo(() => {
    if (!redeemRewardsAuthorizationList) return {};
    const startDate = moment.utc(fromDate);
    const endDate = moment.utc(toDate);
    const groups: { [key: string]: any[] } = {};
    redeemRewardsAuthorizationList.forEach((group: any) => {
      group.data.forEach((item: any) => {
        const itemDate = moment.utc(item.transactionDateTime);

        // Filter by date range
        if (
          itemDate.isSameOrAfter(startDate) &&
          itemDate.isSameOrBefore(endDate)
        ) {
          const dateKey = itemDate.format('YYYY-MM-DD');
          if (!groups[dateKey]) {
            groups[dateKey] = [];
          }
          groups[dateKey].push(item);
        }
      });
    });
    const sortedDateKeys = Object.keys(groups).sort(
      (a, b) => moment.utc(b).valueOf() - moment.utc(a).valueOf()
    );
    const sortedGroups: { [key: string]: any[] } = {};
    sortedDateKeys.forEach((key) => {
      sortedGroups[key] = groups[key];
    });
    return sortedGroups;
  }, [redeemRewardsAuthorizationList, fromDate, toDate]);

  const fetchAuthorizationList = (fromDate: string, toDate: string) => {
    const rewardsPointspayload = {
      breakdownlevel: 'TOTALS',
      pointsRedeem: 'POINTS_REDEEMED',
      currentBalance: 'CURRENT_BALANCE',
      previousBalance: 'PREVIOUS_BALANCE',
      pointEarned: 'POINTS_EARNED',
      accountId: accountId.replace(/^0+/, ''),
      cardLastFourDigits: selectedCard?.cardNumber.slice(-4),
      userIdType: 'RANAC',
      fromDate: fromDate,
      toDate: toDate,
    };
    dispatch(redeemRewardsPointsRequest(rewardsPointspayload));
    const payloadData = {
      accId: accountId.replace(/^0+/, ''),
      cardLastFourDigits: selectedCard?.cardNumber.slice(-4),
      fromDate: fromDate,
      toDate: toDate,
      includeResponse: true,
    };
    dispatch(fetchRedeemRewardsAuthorizationRequest(payloadData));
    const payload = {
      userId: accountId.replace(/^0+/, ''),
      redemptionType: 'PWR',
      userIdType: 'RANAC',
      cardLastFourDigits: selectedCard?.cardNumber.slice(-4),
    };
    dispatch(redeemRewardSearchRequest(payload));
  };

  const handleFinalRedeemRewards = () => {
    if (isSelectionAuthorizationIndexData) {
      logBrazeCustomEvent('action_redeemed_rewards', {
        merchantLocation:
          isSelectionAuthorizationIndexData?.de043CardAcceptorNameLocation,
        transactionAmount:
          isSelectionAuthorizationIndexData?.de004TransactionAmount,
        transactionDate: isSelectionAuthorizationIndexData?.transactionDateTime,
        merchantCategoryName:
          isSelectionAuthorizationIndexData?.merchantCategoryName,
        eligibleRogersRedeemBonus:
          isSelectionAuthorizationIndexData?.eligibleRogersRedeemBonus,
        partiallyRedeemed: isSelectionAuthorizationIndexData?.partiallyRedeemed,
        cashBackAmount: isSelectionAuthorizationIndexData?.cashBackAmount,
        pointsRedeemed: isSelectionAuthorizationIndexData?.pointsRedeemed,
        merchantCountry: isSelectionAuthorizationIndexData?.merchantCountry,
        totalPointsRequired:
          isSelectionAuthorizationIndexData?.totalPointsRequired,
      });
      const payload = {
        id: isSelectionAuthorizationIndexData.id,
        accountId: accountId.replace(/^0+/, ''),
        cardLastFourDigits: selectedCard.cardNumber.slice(-4),
        accountIdType: 'RANAC',
        processedBy: 'system',
      };
      dispatch(authorizationReprocessRequest(payload));
    }
  };

  useEffect(() => {
    if (authorizationReprocess) {
      if (authorizationReprocess.statusCode === 200) {
        setConfirmRedeemVisible(false);
        setRedeemStatusVisible(true);
      }
    }
  }, [authorizationReprocess]);

  useEffect(() => {
    if (authorizationReprocessError) {
      setConfirmRedeemVisible(false);
      setAuthorizationFail(true);
    }
  }, [authorizationReprocessError]);

  useEffect(() => {
    if (confirmRedeemVisible && isSelectionAuthorizationIndexData) {
      logBrazeCustomEvent('viewed_redeem_rewards_details', {
        merchantLocation:
          isSelectionAuthorizationIndexData?.de043CardAcceptorNameLocation,
        transactionAmount:
          isSelectionAuthorizationIndexData?.de004TransactionAmount,
        transactionDate: isSelectionAuthorizationIndexData?.transactionDateTime,
        merchantCategoryName:
          isSelectionAuthorizationIndexData?.merchantCategoryName,
        eligibleRogersRedeemBonus:
          isSelectionAuthorizationIndexData?.eligibleRogersRedeemBonus,
        partiallyRedeemed: isSelectionAuthorizationIndexData?.partiallyRedeemed,
        cashBackAmount: isSelectionAuthorizationIndexData?.cashBackAmount,
        pointsRedeemed: isSelectionAuthorizationIndexData?.pointsRedeemed,
        merchantCountry: isSelectionAuthorizationIndexData?.merchantCountry,
        totalPointsRequired:
          isSelectionAuthorizationIndexData?.totalPointsRequired,
      });
    }
  }, [confirmRedeemVisible, isSelectionAuthorizationIndexData]);

  const updateRedemption = () => {
    dispatch(updateStateAfterRedeeption());
    setRedeemStatusVisible(false);
    dispatch(
      rewardsRequest({
        accountId: accountId,
        cardLastFourDigits: accountDetails?.customer?.cardLast4,
      })
    );
    fetchAuthorizationList(fromDate, toDate);
  };

  const authorizationHandleOk = () => {
    setRedeemErrorVisible(false);
    setAuthorizationFail(false);
    dispatch(updateStateAfterRedeeption());
  };

  const minThreshold =
    redeemRewardSearchData?.redeemerProfile?.preferences
      ?.minAccountPurchaseThreshold;
  const displayThreshold =
    redeemRewardSearchError || minThreshold == null
      ? webFormatCurrency(10.0, lang)
      : webFormatCurrency(Number(minThreshold), lang);

  useEffect(() => {
    if (contactDetails) {
      if (
        contactDetails.availableCards &&
        contactDetails.availableCards.length > 0
      ) {
        setAvailableCards(contactDetails.availableCards);
        if (
          !selectedCard ||
          !contactDetails.availableCards.some(
            (card: any) => card?.customerId === selectedCard?.customerId
          )
        ) {
          setSelectedCard(contactDetails.availableCards[0]);
        }
      }
    }
  }, [contactDetails]);

  useEffect(() => {
    if (
      error ||
      redeemRewardsError ||
      authorizationerror ||
      redeemRewardSearchError ||
      authorizationReprocessError
    ) {
      setShowApiErrorBanner(true);
    } else {
      setShowApiErrorBanner(false);
    }
  }, [
    error,
    redeemRewardsError,
    authorizationerror,
    redeemRewardSearchError,
    authorizationReprocessError,
  ]);

  const breadcrumbs = [
    { name: entries?.rb_navigation?.rewardsLogoTitle, href: '/rewards' },
    {
      name: rb_rewards_cashback?.rewardsCashback?.cashBackRewards,
      href: '/redeem-rewards',
    },
  ];

  const handleClick = () => {
    handleClickEvent('Redemption settings');
    trackEvent(
      EventType.PAGE_VIEW,
      ['modal reward settings', 'rewards'],
      window.location.href
    );
    setIsRewardSettingsOpen(true);
  };

  return (
    <div className="bg-bg-body py-4 md:px-8 px-4">
      {showApiErrorBanner && (
        <div className="w-full sm:px-4 md:px-[40px] py-[10px] bg-[#FFF6F3] flex gap-[20px] items-center absolute top-0 left-0 md:left-20 md:w-[stretch]">
          <div className="flex items-center gap-[20px]">
            {' '}
            <div className="flex-shrink-0">
              <img
                src={assets?.infoToolTip}
                alt="info-icon"
                className="w-6 h-6"
              />
            </div>
            <p
              aria-label={
                rb_rewards_cashback?.rewardsCashback?.rewardsFailureBanner
              }
              className="text-[#5B1C00] text-base font-medium"
            >
              {renderDescriptionParagraphs(
                rb_rewards_cashback?.rewardsCashback?.rewardsFailureBanner
              )}
            </p>
          </div>
          <div className="flex-grow"></div>
          <div
            aria-label={rb_rewards_cashback?.rewardsCashback?.reload}
            className="text-[#DA291C] text-base font-bold cursor-pointer"
            onClick={() => window.location.reload()}
          >
            {rb_rewards_cashback?.rewardsCashback?.reload}
          </div>
          <img
            src={assets?.closeIcon}
            alt="close-icon"
            className="w-6 h-6 cursor-pointer"
            onClick={() => setShowApiErrorBanner(false)}
          />
        </div>
      )}
      <Breadcrumbs items={breadcrumbs} />
      <div className="w-full mx-auto rounded-[15px] bg-white min-h-[70vh] my-0">
        <div className="bg-white md:p-6 p-4 rounded-lg">
          {loading || contactDetailsLoading || redeemRewardSearchLoading ? (
            <div className="flex justify-center items-center h-[80vh]">
              <Spinner />
            </div>
          ) : (
            <>
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <h2
                  aria-label={renderDescriptionParagraphs(
                    entries?.rb_rewards_cashback?.rewardsCashback
                      ?.cashBackRewards,
                    ' ',
                    true
                  )}
                  className="text-[22px] font-semibold leading-[28px] tracking-[0.35%] md:pb-4 pb-3"
                >
                  {renderDescriptionParagraphs(
                    entries?.rb_rewards_cashback?.rewardsCashback
                      ?.cashBackRewards
                  )}
                </h2>

                <button
                  aria-label={rb_rewards_cashback?.rewardsCashback?.faqsCta}
                  className="text-[#DA291C] hover:bg-red-100 text-base font-semibold px-3 py-2 md:mb-4 mb-2.5 border border-[#DA291C] self-start md:self-auto w-full md:w-auto"
                  onClick={() =>
                    window.open(
                      rb_links?.rbLinksCollection
                        ?.more_legal_privacy_rewards_tc_web_link,
                      '_blank'
                    )
                  }
                >
                  {rb_rewards_cashback?.rewardsCashback?.faqsCta}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full mb-4">
                <div className="flex items-center gap-6 md:bg-[#F5F5F5] rounded-[15px] md:px-6 md:py-4 md:h-[122.57px] overflow-hidden">
                  <img
                    src={cardStructure?.imgUrl}
                    alt="Card"
                    className="h-[72px] w-auto shrink-0 hidden md:block"
                  />
                  <div className="flex flex-col justify-center flex-1">
                    <div className="text-[#1F1F1F] text-[13px] md:text-[16px] font-semibold leading-[20px] md:leading-[24px] break-words">
                      {renderDescriptionParagraphs(productWithCardLast4)}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between md:justify-center border border-[#E9E9E9] rounded-[15px] bg-white px-5 md:px-6 py-4 h-[122.57px] overflow-visible">
                  <div className="flex items-center gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-[#DA291C] text-[#DA291C] text-xl font-semibold">
                      $
                    </div>
                    <div className="flex flex-col">
                      <div
                        aria-label={`${rb_rewards?.rewardsBalanceTitle} ${rb_rewards?.rewardsBalanceIndofText}`}
                        className="text-[#1F1F1F] text-base font-bold leading-[20px] flex items-center gap-1"
                      >
                        {rb_rewards?.rewardsBalanceTitle}
                        <div>
                          <CommanToolTip
                            label={''}
                            tooltipText={rb_rewards?.rewardsBalanceIndofText
                              .split(/<br\s*\/?>/i)
                              .filter(Boolean)}
                            isVisible={false}
                            fill="#DA291C"
                            toolTipStyle={{ maxWidth: '250px', left: '-150px' }}
                          />
                        </div>
                      </div>
                      <div
                        aria-label={rewardBalance}
                        className="text-[#1F1F1F] text-base font-medium leading-[24px] flex gap-1"
                      >
                        {rewardBalance === null || rewardBalance === undefined
                          ? lang?.includes('en')
                            ? '$-.--'
                            : '-.--$'
                          : Number(rewardBalance) < 0
                            ? `-${webFormatCurrency(Math.abs(Number(rewardBalance)), lang)}`
                            : webFormatCurrency(Number(rewardBalance), lang) ||
                              '--'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {rewardBalance < 10 && (
                <div className="bg-[#FFF6F3] w-full rounded-lg p-4 py-6 mb-4">
                  <p
                    aria-label={
                      rb_rewards_cashback?.rewardsCashback?.lowRewardBalance
                    }
                    className="text-[#AE2116] text-base font-semibold"
                  >
                    {rb_rewards_cashback?.rewardsCashback?.lowRewardBalance}
                  </p>
                  <p
                    aria-label={
                      rb_rewards_cashback?.rewardsCashback?.lowRewardBalanceDesc
                    }
                    className="text-[15px] text-[#1f1f1f] font-medium"
                  >
                    {rb_rewards_cashback?.rewardsCashback?.lowRewardBalanceDesc}
                  </p>
                </div>
              )}

              <div className="flex flex-col lg:flex-row gap-4 mb-4">
                <div className="md:border rounded-lg w-full border-0">
                  <div className="flex flex-col md:flex-row items-center gap-4 p-4 justify-between">
                    <div
                      aria-label={
                        rb_rewards_cashback?.rewardsCashback
                          ?.minAmountRedemption
                      }
                      className="text-[#DA291C] text-sm font-bold leading-[20px] flex items-center gap-1"
                    >
                      {
                        rb_rewards_cashback?.rewardsCashback
                          ?.minAmountRedemption
                      }
                      {displayThreshold}
                      <CommanToolTip
                        label={''}
                        tooltipText={
                          rb_rewards_cashback?.rewardsCashback?.rbRedeemInfo
                        }
                        isVisible={false}
                        fill="#DA291C"
                        toolTipStyle={{ left: '-100px' }}
                      />
                    </div>
                    <div>
                      <Button
                        variant="solid"
                        className={`h-[48px] w-40 px-[12px] py-[20px] justify-center flex items-center relative ${redeemRewardSearchError && 'bg-gray-300 hover:bg-gray-300'}`}
                        onClick={
                          redeemRewardSearchError ? undefined : handleClick
                        }
                      >
                        <span
                          aria-label={
                            rb_rewards_cashback?.rewardsCashback?.rewardSettings
                          }
                        >
                          {rb_rewards_cashback?.rewardsCashback?.rewardSettings}
                        </span>
                      </Button>
                    </div>
                  </div>
                </div>
                {availableCards?.length === 1 && (
                  <div className="w-full p-4 border-b rounded-lg relative bg-[#F5F5F5]">
                    <div
                      aria-label={rb_transaction_history?.showingTitle}
                      className="text-[#1F1F1F] text-[15px] font-normal leading-[24px]"
                    >
                      {rb_transaction_history?.showingTitle}
                    </div>
                    <p
                      aria-label={`${selectedCard?.nameOnCard} ${selectedCard?.cardNumber?.slice(-4)}`}
                      className="w-full flex justify-between pb-[8px] border-b-[1px] border-[#999999] text-[#1F1F1F] text-[16px] font-[650] leading-[24px] tracking-[-0.051px] "
                    >
                      {selectedCard?.nameOnCard +
                        '... ' +
                        selectedCard?.cardNumber?.slice(-4)}
                    </p>
                  </div>
                )}
                {availableCards?.length > 1 && (
                  <div className="w-full p-4 border-b rounded-lg relative bg-[#F5F5F5]">
                    <div
                      aria-label={rb_transaction_history?.showingTitle}
                      className="text-[#1F1F1F] text-[15px] font-normal leading-[24px]"
                    >
                      {rb_transaction_history?.showingTitle}
                    </div>
                    <CardDropdown
                      className="w-full lg:mb-[-10px]"
                      availableCards={availableCards}
                      selectedCard={selectedCard}
                      setSelectedCard={setSelectedCard}
                    />
                  </div>
                )}
              </div>
              <div
                aria-label={
                  rb_rewards_cashback?.rewardsCashback?.eligibleCashBackMessage
                }
                className="bg-[#F1FDFF] w-full rounded-lg p-4 py-6 text-[15px] text-[#1F1F1F] font-normal mb-4"
              >
                {rb_rewards_cashback?.rewardsCashback?.eligibleCashBackMessage}
              </div>

              {redeemRewardsAuthorizationLoding ? (
                <div className="flex h-[30vh] justify-center items-center">
                  <Spinner />
                </div>
              ) : Object.keys(groupedTransactions).length ? (
                <>
                  <div className="hidden md:block mt-4">
                    <table className="min-w-full ">
                      <thead className="bg-white border-b text-left text-sm font-medium text-[#666666]">
                        <tr>
                          <th
                            aria-label={
                              rb_rewards_cashback?.rewardsCashback?.date
                            }
                            className="p-4"
                          >
                            {rb_rewards_cashback?.rewardsCashback?.date}
                          </th>
                          <th
                            aria-label={
                              rb_rewards_cashback?.rewardsCashback
                                ?.transactionDesc
                            }
                            className="p-4"
                          >
                            {
                              rb_rewards_cashback?.rewardsCashback
                                ?.transactionDesc
                            }
                          </th>
                          <th
                            aria-label={
                              rb_rewards_cashback?.rewardsCashback?.amount
                            }
                            className="p-4"
                          >
                            {rb_rewards_cashback?.rewardsCashback?.amount}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {Object.entries(groupedTransactions).map(
                          ([dateKey, transactionsOnDate]: [string, any[]]) => (
                            <React.Fragment key={dateKey}>
                              {transactionsOnDate.map(
                                (reward: any, idx: number) => (
                                  <RewardTransactionItem
                                    key={reward.id || reward.externalId || idx}
                                    authorizationItem={reward}
                                    rb_rewards_cashback={rb_rewards_cashback}
                                    redeemRewardSearchData={
                                      redeemRewardSearchData
                                    }
                                    rewardBalance={rewardBalance}
                                    setRedeemErrorVisible={
                                      setRedeemErrorVisible
                                    }
                                    setSelectionAuthorizationIndexData={
                                      setSelectionAuthorizationIndexData
                                    }
                                    setConfirmRedeemVisible={
                                      setConfirmRedeemVisible
                                    }
                                    lang={lang}
                                    displayMode="table"
                                    formattedDate={formatTableDisplayDate(
                                      dateKey
                                    )}
                                  />
                                )
                              )}
                            </React.Fragment>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="md:hidden space-y-4">
                    {Object.entries(groupedTransactions).map(
                      ([dateKey, transactionsOnDate]: [string, any[]]) => (
                        <div className="space-y-4" key={dateKey}>
                          <div className="text-xl font-semibold text-left bg-gray-200 py-2">
                            {formatMobileDisplayDate(dateKey, locale)}
                          </div>
                          {transactionsOnDate.map(
                            (reward: any, idx: number) => (
                              <RewardTransactionItem
                                key={reward.id || reward.externalId || idx}
                                authorizationItem={reward}
                                rb_rewards_cashback={rb_rewards_cashback}
                                redeemRewardSearchData={redeemRewardSearchData}
                                rewardBalance={rewardBalance}
                                setRedeemErrorVisible={setRedeemErrorVisible}
                                setSelectionAuthorizationIndexData={
                                  setSelectionAuthorizationIndexData
                                }
                                setConfirmRedeemVisible={
                                  setConfirmRedeemVisible
                                }
                                lang={lang}
                                displayMode="list"
                              />
                            )
                          )}
                        </div>
                      )
                    )}
                  </div>
                </>
              ) : (
                <div
                  aria-label={
                    rb_rewards_cashback?.rewardsCashback?.noEligibleRedemptions
                  }
                  className="text-center py-4"
                >
                  <p>
                    {
                      rb_rewards_cashback?.rewardsCashback
                        ?.noEligibleRedemptions
                    }
                  </p>
                  <p>
                    {
                      rb_rewards_cashback?.rewardsCashback
                        ?.notEligibleRedemptions
                    }
                  </p>
                </div>
              )}
              <GoogleReCaptchaProvider
                reCaptchaKey={environment.recaptchaSiteKey}
                language={recaptchaLang}
                container={{
                  parameters: {
                    badge: 'bottomright',
                    theme: 'light',
                  },
                }}
                scriptProps={{ async: true, defer: true }}
              >
                <RewardSettingPopup
                  isOpen={isRewardSettingsOpen}
                  closeModal={() => setIsRewardSettingsOpen(false)}
                  selectedCard={selectedCard}
                  setRewardSettingSuccessModal={setRewardSettingSuccessModal}
                />
              </GoogleReCaptchaProvider>
              {rewardSettingSuccessModal && (
                <SuccessModel
                  isOpen={rewardSettingSuccessModal}
                  title={entries.rb_rewards_setting?.rewardsSetting?.successMsg}
                  subTitle={''}
                  successIcon={true}
                  closeModal={() => setRewardSettingSuccessModal(false)}
                  errorIcon={false}
                  button={false}
                />
              )}
            </>
          )}
        </div>
      </div>

      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="md:block hidden fixed bottom-40 right-10 border rounded-lg border-[#DA291C] w-16 text-center mb-20 p-3 shadow-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 z-50"
          aria-label="Back to top"
        >
          <img src={assets?.upArrow} alt="upArrow" className="w-6 h-6 ml-2" />
          <h2
            aria-label={rb_rewards_cashback?.rewardsCashback?.toTop}
            className="text-[#000] text-sm font-semibold"
          >
            {rb_rewards_cashback?.rewardsCashback?.toTop}
          </h2>
        </button>
      )}

      <CommanModal
        isOpen={redeemErrorVisible || authorizationFail}
        jsxContent={
          <>
            <div
              aria-label={
                redeemErrorVisible
                  ? rb_rewards_cashback?.rewardsCashback?.redemptionErrorTitle
                  : (rb_error?.rewards[
                      authorizationReprocessError?.title
                        ?.toUpperCase()
                        ?.replace(/\s+/g, '_')
                    ]?.title ??
                    authorizationReprocessError?.title ??
                    '')
              }
              className="mt-[20px] text-lg font-semibold text-center"
            >
              {redeemErrorVisible
                ? rb_rewards_cashback?.rewardsCashback?.redemptionErrorTitle
                : (rb_error?.rewards[
                    authorizationReprocessError?.title
                      ?.toUpperCase()
                      ?.replace(/\s+/g, '_')
                  ]?.title ??
                  authorizationReprocessError?.title ??
                  '')}
            </div>
            <div
              aria-label={
                redeemErrorVisible
                  ? rb_rewards_cashback?.rewardsCashback?.redemptionErrorMessage
                  : (rb_error?.rewards[
                      authorizationReprocessError?.title
                        ?.toUpperCase()
                        ?.replace(/\s+/g, '_')
                    ]?.detail ??
                    authorizationReprocessError?.detail ??
                    '')
              }
              className="mt-[20px]"
            >
              {redeemErrorVisible
                ? rb_rewards_cashback?.rewardsCashback?.redemptionErrorMessage
                : (rb_error?.rewards[
                    authorizationReprocessError?.title
                      ?.toUpperCase()
                      ?.replace(/\s+/g, '_')
                  ]?.detail ??
                  authorizationReprocessError?.detail ??
                  '')}
            </div>
          </>
        }
        onClose={() => {
          authorizationHandleOk();
        }}
        buttonText={rb_rewards_cashback?.rewardsCashback?.ok}
        cardStatus={true}
        closeIcon={false}
      />

      {confirmRedeemVisible && (
        <Modal
          isOpen={confirmRedeemVisible}
          contentLabel="redeemRewards"
          className="fixed p-0 w-[370px] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 outline-none"
          overlayClassName="fixed inset-0 bg-black bg-opacity-50"
        >
          <div className="flex flex-col justify-center items-center gap-[20px] p-[20px] rounded-lg bg-white overflow-auto relative">
            <img
              src={assets?.redeem_reward_icon}
              alt="rewardsIcon"
              className="w-20 h-20"
            />
            <h3
              aria-label={rb_rewards_cashback.rewardsCashback.confirmRedemption}
              className="text-[#1F1F1F] text-[18px] font-[500] leading-[25px] tracking-[0.068px] text-center"
            >
              {rb_rewards_cashback.rewardsCashback.confirmRedemption}
            </h3>
            <p
              aria-label={`${rb_rewards_cashback.rewardsCashback.redeemCashbackForPurchase + isSelectionAuthorizationIndexData?.de043CardAcceptorNameLocation} ${'?'}`}
              className="text-[#1F1F1F] text-[16px] font-[400] leading-[21px] tracking-[-0.051px] text-center"
            >
              {`${rb_rewards_cashback.rewardsCashback.redeemCashbackForPurchase + isSelectionAuthorizationIndexData?.de043CardAcceptorNameLocation} ${'?'}`}
            </p>
            {redeemRewardsAuthorizationList &&
            redeemRewardsAuthorizationList.some(
              (group: any) =>
                group.data &&
                group.data.some(
                  (item: any) => item?.eligibleRogersRedeemBonus === true
                )
            ) ? (
              <p className="text-[#1F1F1F] bg-[#F1FDFF] w-full text-[16px] font-[500] leading-[21px] tracking-[-0.051px] text-center">
                <span aria-label={rb_rewards_cashback.rewardsCashback.reminder}>
                  {rb_rewards_cashback.rewardsCashback.reminder}
                </span>
                <br />
                {isSelectionAuthorizationIndexData?.eligibleRogersRedeemBonus ===
                true ? (
                  <span
                    aria-label={
                      rb_rewards_cashback.rewardsCashback.getBonusRedeemingDis
                    }
                  >
                    {rb_rewards_cashback.rewardsCashback.getBonusRedeemingDis}
                  </span>
                ) : (
                  <span
                    aria-label={
                      rb_rewards_cashback.rewardsCashback.redemptionForNonRogers
                    }
                  >
                    {rb_rewards_cashback.rewardsCashback.redemptionForNonRogers}
                  </span>
                )}
              </p>
            ) : null}{' '}
            <button
              aria-label={rb_rewards_cashback.rewardsCashback.redeem}
              type="button"
              onClick={() => {
                handleFinalRedeemRewards();
              }}
              disabled={authorizationReprocessLoding}
              className={`${authorizationReprocessLoding ? 'bg-disabled-bg-color text-Text-disabled-btn border-[#D9D9D9] border' : 'text-white bg-primary-red'} w-full text-center flex-1 items-center align-center px-[20px] py-[12px]`}
            >
              {rb_rewards_cashback.rewardsCashback.redeem}
            </button>
            <button
              aria-label={rb_rewards_cashback.rewardsCashback.cancel}
              type="button"
              onClick={() => {
                setConfirmRedeemVisible(false);
                setSelectionAuthorizationIndexData(null);
              }}
              disabled={authorizationReprocessLoding}
              className="text-center flex-1 items-center align-center text-[#DA291C] px-[20px] py-[12px] bg-[#fff] w-full border border-[#DA291C]"
            >
              {rb_rewards_cashback.rewardsCashback.cancel}
            </button>
          </div>
        </Modal>
      )}

      {redeemStatusVisible && (
        <Modal
          isOpen={redeemStatusVisible}
          contentLabel="redeemRewards"
          className="fixed p-0 w-[370px] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 outline-none"
          overlayClassName="fixed inset-0 bg-black bg-opacity-50"
        >
          <div className="flex flex-col justify-center items-center gap-[20px] p-[20px] rounded-lg bg-white overflow-auto relative">
            <img
              src={assets?.device_recogonized_success_popup_icon}
              alt="rewardsIcon"
              className="w-20 h-20"
            />
            <h3
              aria-label={rb_rewards_cashback.rewardsCashback.success}
              className="text-[#1F1F1F] text-[18px] font-[500] leading-[25px] tracking-[0.068px] text-center"
            >
              {rb_rewards_cashback.rewardsCashback.success}!
            </h3>
            <p
              aria-label={
                rb_rewards_cashback.rewardsCashback.yourRedemptionCompleted
              }
              className="text-[#1F1F1F] text-[16px] font-[400] leading-[21px] tracking-[-0.051px] text-center"
            >
              {rb_rewards_cashback.rewardsCashback.yourRedemptionCompleted}
            </p>
            <button
              aria-label={rb_rewards_cashback.rewardsCashback.ok}
              type="button"
              onClick={() => {
                updateRedemption();
              }}
              className="text-center flex-1 items-center align-center text-white px-[20px] py-[12px] bg-primary-red w-full"
            >
              {rb_rewards_cashback.rewardsCashback.ok}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CashbackRewards;
